/* eslint-disable react-refresh/only-export-components */
import React from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Toggle } from "@/components/custom/toggle";
import { cn } from "@/lib/utils";
import { useTimeSettings } from "@/state/time-settings";

/**
 * Day-of-week recurrence for scheduled trainings.
 *
 * `recurringDays` holds `DayOfWeek` names - the same vocabulary and Monday-first order the site
 * operational-hours schedules use - and the backend evaluates them in the account timezone. Null
 * means no weekday restriction, which is the plain "every `recurring` days" behaviour, so `recurring`
 * stays 1 for Every Day and 7 for Every Week / Custom Days.
 */

/** Monday-first, mirroring `DAYS_OF_WEEK` in the operational-hours editor. */
export const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

export type DayOfWeek = (typeof DAYS_OF_WEEK)[number];

export const RECURRING_MODES = ["everyDay", "everyWeek", "customDays"] as const;
export type RecurringMode = (typeof RECURRING_MODES)[number];

export type RecurrenceData = {
  recurring?: number | null;
  recurringDays?: DayOfWeek[] | null;
  /**
   * UI-only: which preset the user picked. Only needed to tell "Custom Days with nothing ticked yet"
   * (invalid, and the reason submit is blocked) apart from the Every Week preset - neither restricts
   * the weekdays. Stripped before the request, like `runNow`.
   */
  recurringMode?: RecurringMode | null;
};

/** Monday-first, de-duplicated; null and [] both mean "no weekday restriction". */
export const normalizeDays = (days?: DayOfWeek[] | null): DayOfWeek[] => {
  if (!days?.length) return [];
  const named = new Set(days);
  return DAYS_OF_WEEK.filter((day) => named.has(day));
};

/** What the API stores: null rather than an empty list when nothing is restricted. */
export const toStoredDays = (days?: DayOfWeek[] | null): DayOfWeek[] | null => {
  const normalized = normalizeDays(days);
  return normalized.length ? normalized : null;
};

export const recurringModeOf = ({
  recurring,
  recurringDays,
}: RecurrenceData): RecurringMode =>
  recurringDays?.length
    ? "customDays"
    : recurring === 1
      ? "everyDay"
      : "everyWeek";

/** The form values a preset maps to. Custom Days keeps whatever is already ticked. */
export const recurringModeValues = (
  mode: RecurringMode,
  current: RecurrenceData,
  fallbackDay: DayOfWeek,
): {
  recurring: number;
  recurringDays: DayOfWeek[] | null;
  recurringMode: RecurringMode;
} => {
  if (mode === "everyDay")
    return { recurring: 1, recurringDays: null, recurringMode: mode };
  if (mode === "everyWeek")
    return { recurring: 7, recurringDays: null, recurringMode: mode };
  const kept = normalizeDays(current.recurringDays);
  return {
    recurring: 7,
    recurringDays: kept.length ? kept : [fallbackDay],
    recurringMode: mode,
  };
};

/**
 * The weekday a moment falls on in `timeZone` (the dashboard's display zone), not in the browser's.
 * Same resolution the backend uses, so the ticked chip is the day the training will run on.
 */
export const dayOfWeekIn = (date: Date, timeZone?: string): DayOfWeek =>
  new Intl.DateTimeFormat("en-US", { timeZone, weekday: "long" }).format(
    date,
  ) as DayOfWeek;

const timeIn = (date: Date, timeZone?: string, locale?: string) =>
  new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  }).format(date);

/** Short localized weekday names, keyed by `DayOfWeek`. */
export function useWeekdayLabels(): Record<DayOfWeek, string> {
  const { i18n } = useTranslation();
  return React.useMemo(() => {
    const fmt = new Intl.DateTimeFormat(i18n.language, {
      weekday: "short",
      timeZone: "UTC",
    });
    // 2024-01-01 was a Monday, so the day-of-month offset follows DAYS_OF_WEEK
    return Object.fromEntries(
      DAYS_OF_WEEK.map((day, i) => [
        day,
        fmt.format(new Date(Date.UTC(2024, 0, 1 + i))),
      ]),
    ) as Record<DayOfWeek, string>;
  }, [i18n.language]);
}

/** Human-readable recurrence, e.g. "Weekly on Mon, Wed, Fri at 21:00". */
export function useRecurrenceSummary() {
  const { t, i18n } = useTranslation();
  const { timeZone } = useTimeSettings();
  const labels = useWeekdayLabels();

  return React.useCallback(
    (data: RecurrenceData & { scheduledFor?: Date | string | null }) => {
      const recurring = data.recurring ?? -1;
      if (recurring < 0) return t("draft");

      const at = data.scheduledFor ? new Date(data.scheduledFor) : undefined;
      const time =
        at && !isNaN(+at)
          ? ` ${t("atTime")} ${timeIn(at, timeZone, i18n.language)}`
          : "";

      if (recurring === 0) return `${t("once")}${time}`;

      const days = normalizeDays(data.recurringDays);
      if (days.length) {
        const named = days.map((day) => labels[day]).join(", ");
        return `${t("weeklyOn")} ${named}${time}`;
      }
      return `${recurring === 1 ? t("everyDay") : t("everyWeek")}${time}`;
    },
    [t, i18n.language, timeZone, labels],
  );
}

export function WeekdayPicker({
  value,
  onChange,
  disabled,
  className,
}: {
  value?: DayOfWeek[] | null;
  onChange: (days: DayOfWeek[]) => void;
  disabled?: boolean;
  className?: string;
}) {
  const labels = useWeekdayLabels();
  // Toggling a set rather than filtering a list, so a value that arrived with repeats (or a click
  // landing on a stale render) can only ever emit a clean, Monday-first selection.
  const selected = React.useMemo(() => new Set(normalizeDays(value)), [value]);
  return (
    <div className={cn("flex flex-wrap items-center gap-1", className)}>
      {DAYS_OF_WEEK.map((day) => (
        <Toggle
          key={day}
          size="sm"
          disabled={disabled}
          pressed={selected.has(day)}
          onPressedChange={(on) => {
            const next = new Set(selected);
            if (on) next.add(day);
            else next.delete(day);
            onChange(DAYS_OF_WEEK.filter((d) => next.has(d)));
          }}
        >
          {labels[day]}
        </Toggle>
      ))}
    </div>
  );
}

/**
 * Shared zod rule: Custom Days needs at least one weekday. Attached to the schedule forms so
 * `handleSubmit` (and the wizard's per-step `trigger`) refuse an empty selection.
 */
export const refineRecurrence = (value: unknown, ctx: z.RefinementCtx) => {
  const { recurringMode, recurringDays } = (value ?? {}) as RecurrenceData;
  if (recurringMode === "customDays" && !recurringDays?.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["recurringDays"],
      message: "Select at least one day",
    });
  }
};

export const RecurrenceSchema = z
  .object({
    recurring: z.number().optional().nullable(),
    recurringDays: z.array(z.enum(DAYS_OF_WEEK)).optional().nullable(),
    recurringMode: z.enum(RECURRING_MODES).optional().nullable(),
  })
  .passthrough()
  .superRefine(refineRecurrence);
